import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Link,
  CircularProgress
} from '@mui/material';
import { convertDateFormat, extractTimeFromTimestamp } from '../../utils/services';
import ApiCall from '../../utils/Api';

const FundUnitsTable = ({ data, loading, onRefresh }) => {
  const [ethToUsdRate, setEthToUsdRate] = useState(3500); // Default rate

  // Fetch ETH to USD conversion rate
  useEffect(() => {
    const fetchEthRate = async () => {
      try {
        const response = await ApiCall.Api.get('/thirdPartyApi/getCryptoData');
        const ethereumData = response.data.data['1027'];
        const usdPrice = ethereumData.quote.USD.price;
        setEthToUsdRate(usdPrice);
      } catch (error) {
        console.error('Error fetching ETH rate:', error);
      }
    };

    // Fetch immediately
    fetchEthRate();
    
    // Refresh every 30 seconds to keep prices current
    const interval = setInterval(fetchEthRate, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Helper function to flatten rows - each pricing level becomes a separate row
  const flattenRowsWithLevels = (rows) => {
    const flattened = [];
    rows.forEach((row) => {
      const pricingLevels = row?.pricingLevels || row?.pricing_levels || row?.levels || [];
      const pricingType = row?.pricingType || row?.pricing_type;
      
      // Only create separate rows if it's a multi-level fund with pricing levels
      if (pricingType === 'levels' && pricingLevels && pricingLevels.length > 0) {
        // Multi-level fund: create a row for each level
        pricingLevels.forEach((level, idx) => {
          flattened.push({
            ...row,
            level: level,
            levelIndex: idx,
            isLevelRow: true
          });
        });
      } else {
        // Single level or fixed pricing: create one row without level data
        flattened.push({
          ...row,
          level: null,
          levelIndex: -1,
          isLevelRow: false
        });
      }
    });
    return flattened;
  };

  // Format yield per year (show "240k" for large numbers)
  const formatYieldTDUX = (yieldValue) => {
    if (yieldValue >= 1000000) return `${(yieldValue / 1000000).toFixed(1)}M`;
    if (yieldValue >= 1000) return `${(yieldValue / 1000).toFixed(0)}k`;
    return Math.floor(yieldValue).toString();
  };

  // Flatten rows for rendering - each pricing level becomes a separate row
  const flattenedRows = data && data.length > 0 ? flattenRowsWithLevels(data) : [];

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'available':
        return 'success';
      case 'sold out':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Get contract address for explorer link
  const getExplorerLink = (row) => {
    const contractAddr = row?.contractAddress || row?.contract_address || 
                         row?.tokenizedFundAddress;
    return contractAddr 
      ? `https://sepolia.etherscan.io/address/${contractAddr}`
      : null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TableContainer 
      component={Paper} 
      sx={{ 
        mt: 2,
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflowX: 'auto'
      }}
    >
      <Table sx={{ minWidth: 1200 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'black' }}>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              color: 'white',
              minWidth: '180px',
              padding: '16px 12px'
            }}>
              Creation
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              color: 'white',
              minWidth: '100px',
              padding: '16px 12px'
            }}>
              Code
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              color: 'white',
              minWidth: '200px',
              padding: '16px 12px'
            }}>
              Fund
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              color: 'white',
              minWidth: '120px',
              padding: '16px 12px'
            }}>
              Level
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              color: 'white',
              minWidth: '120px',
              padding: '16px 12px'
            }} align="right">
              Coin Price
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              color: 'white',
              minWidth: '180px',
              padding: '16px 12px'
            }} align="right">
              Ticket Size (Min Inv.)
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              color: 'white',
              minWidth: '150px',
              padding: '16px 12px'
            }} align="right">
              Investment
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              color: 'white',
              minWidth: '150px',
              padding: '16px 12px'
            }} align="right">
              Yield
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              color: 'white',
              minWidth: '100px',
              padding: '16px 12px'
            }} align="center">
              Status
            </TableCell>
            <TableCell sx={{ 
              fontWeight: '600', 
              fontSize: '14px',
              color: 'white',
              minWidth: '200px',
              padding: '16px 12px'
            }}>
              Links
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!data || data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ padding: '40px' }}>
                <Typography variant="body1" color="text.secondary" fontSize="16px">
                  No fund units found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            flattenedRows.map((row, index) => {
              const level = row.level;
              const levelIndex = row.levelIndex;
              
              // Calculate values based on level or base row
              let ticketUSD = 0;
              let ticketETH = 0;
              
              if (level) {
                ticketUSD = level?.ticketUSD || level?.ticket_usd || 0;
                ticketETH = level?.ticketETH || level?.ticket_eth || 0;
                // If ETH not provided, calculate from USD
                if (!ticketETH && ticketUSD && ethToUsdRate) {
                  ticketETH = ticketUSD / ethToUsdRate;
                }
              } else if (row.ticketUSD || row.ticket_usd) {
                ticketUSD = row.ticketUSD || row.ticket_usd || 0;
                if (ethToUsdRate) {
                  ticketETH = ticketUSD / ethToUsdRate;
                }
              }
              
              const annualYield = level?.annualYieldPercent || level?.annual_yield_percent || 
                                  row?.annualYield || 0;
              
              // Coin Price - try to get from level or row
              const coinPriceUSD = level?.coinPriceUSD || level?.coin_price_usd || 
                                   row?.coinPriceUSD || row?.coin_price_usd || 
                                   0.10; // Default fallback
              
              // Investment in TDUX = Ticket Size USD / Coin Price USD
              const investmentTDUX = ticketUSD && coinPriceUSD ? Math.floor(ticketUSD / coinPriceUSD) : 0;
              
              // Yield per year in TDUX = (Investment TDUX * Yield %) / 100
              const yieldPerYearTDUX = investmentTDUX && annualYield ? (investmentTDUX * annualYield) / 100 : 0;
              
              // Fund name - remove "- Multi-Level Fund" if present
              let fundName = row?.fundUnitName || row?.name || 'N/A';
              fundName = fundName.replace(/\s*-\s*Multi-Level Fund\s*/i, '');
              
              // Get explorer link
              const explorerLink = getExplorerLink(row);
              
              return (
                <TableRow 
                  key={`${row._id}_${levelIndex}`}
                  hover
                  sx={{ 
                    '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                    '&:hover': { backgroundColor: '#f0f8ff' }
                  }}
                >
                  <TableCell sx={{ padding: '12px' }}>
                    <Typography variant="body2" fontSize="13px">
                      {convertDateFormat(row.createdAt)} {extractTimeFromTimestamp(row.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ padding: '12px' }}>
                    <Typography variant="body2" fontWeight="500" fontSize="13px">
                      {row?.providerData?.institutionCode || row?.providerData?.code || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ padding: '12px' }}>
                    <Typography variant="body2" fontWeight="500" fontSize="13px">
                      {fundName}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ padding: '12px' }}>
                    <Typography variant="body2" fontSize="13px">
                      {level ? `Level ${level.levelName || level.level_name || (levelIndex + 1)}` : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ padding: '12px' }} align="right">
                    <Typography variant="body2" fontSize="13px">
                      {level ? `${isNaN(coinPriceUSD) ? '0.00' : Number(coinPriceUSD).toFixed(2)} USD` : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ padding: '12px' }} align="right">
                    {level ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="body2" fontWeight="600" fontSize="13px">
                          {isNaN(ticketETH) ? '0.00000' : Number(ticketETH).toFixed(5)} ETH
                        </Typography>
                        <Typography variant="body2" fontSize="13px" sx={{ color: 'gray' }}>
                          {isNaN(ticketUSD) ? '0.00' : Number(ticketUSD).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" fontSize="13px">-</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ padding: '12px' }} align="right">
                    {level ? (
                      <Typography variant="body2" fontWeight="600" fontSize="13px">
                        {isNaN(investmentTDUX) ? '0' : Number(investmentTDUX).toLocaleString()} TDUX
                      </Typography>
                    ) : (
                      <Typography variant="body2" fontSize="13px">-</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ padding: '12px' }} align="right">
                    {level ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="body2" fontWeight="600" fontSize="13px">
                          {isNaN(annualYield) ? '0' : Number(annualYield)}%
                        </Typography>
                        <Typography variant="body2" fontSize="13px" sx={{ color: 'gray' }}>
                          {formatYieldTDUX(yieldPerYearTDUX)} TDUX /year
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" fontSize="13px">-</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ padding: '12px' }} align="center">
                    <Chip 
                      label={row.available ? 'Available' : 'Sold Out'} 
                      color={getStatusColor(row.available ? 'available' : 'sold out')}
                      size="small"
                      sx={{ fontSize: '11px', height: '24px' }}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: '12px' }}>
                    {explorerLink ? (
                      <Link 
                        href={explorerLink}
                        target="_blank" 
                        rel="noopener noreferrer"
                        variant="body2"
                        sx={{ fontSize: '13px', textDecoration: 'none', color: '#1976d2' }}
                      >
                        View Fund Unit on Explorer
                      </Link>
                    ) : (
                      <Typography variant="body2" color="text.secondary" fontSize="13px">
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FundUnitsTable;
